import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, money } from "@/lib/api";
import { useCart, FREE_SHIP_THRESHOLD } from "@/context/CartContext";
import { Truck, ShieldCheck, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [mainImg, setMainImg] = useState(null);

  useEffect(() => {
    api.get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        setColor(r.data.colors?.length ? r.data.colors[0] : null);
        setSize(r.data.sizes?.length === 1 ? r.data.sizes[0] : null);
        setMainImg(r.data.images?.[0] || r.data.image || null);
        window.scrollTo(0, 0);
      })
      .catch(() => navigate("/shop/all"));
  }, [slug, navigate]);

  const selectedVariant = useMemo(() => {
    if (!product) return null;
    return product.variants?.find(
      (v) => v.size === size && (!product.colors?.length || v.color === color)
    );
  }, [product, size, color]);

  if (!product) return <div className="pt-40 text-center uppercase tracking-widest text-gray-400">Loading...</div>;

  const baseImages = product.images?.length ? product.images : [product.image].filter(Boolean);
  const variantImages = (product.variants || []).map((v) => v.image).filter(Boolean);
  const gallery = [...new Set([...baseImages, ...variantImages])];
  const activeSrc = mainImg || gallery[0];
  const soldOut = product.available === false;

  const handleColor = (c) => {
    setColor(c);
    const v = product.variants?.find((x) => x.color === c && x.image);
    if (v) setMainImg(v.image);
  };

  const handleAdd = () => {
    if (soldOut) return;
    if (!size) { toast.error("Please select a size"); return; }
    if (!selectedVariant) { toast.error("This combination is unavailable"); return; }
    addItem(product, selectedVariant, 1);
    toast.success(`${product.name} (${size}) added to bag`);
  };

  return (
    <div className="pt-20" data-testid="product-detail-page">
      <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-400 mb-6">
          <Link to="/" className="hover:text-ink">Home</Link><ChevronRight size={12} />
          <Link to={`/shop/${product.category === "unisex" ? "all" : product.category}`} className="hover:text-ink capitalize">{product.category}</Link><ChevronRight size={12} />
          <span className="text-ink line-clamp-1">{product.name}</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-16">
          <div className="flex flex-col-reverse md:flex-row gap-4">
            {gallery.length > 1 && (
              <div className="flex md:flex-col gap-3">
                {gallery.slice(0, 6).map((img, i) => (
                  <button key={i} onClick={() => setMainImg(img)} className={`w-20 h-24 overflow-hidden border-2 ${activeSrc === img ? "border-ink" : "border-transparent"}`} data-testid={`thumb-${i}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <div className="flex-1 aspect-[4/5] bg-[#f4f4f2] overflow-hidden">
              <img src={activeSrc} alt={product.name} className="w-full h-full object-cover" />
            </div>
          </div>

          <div className="flex flex-col">
            {product.badge && <span className="text-xs font-bold uppercase tracking-widest text-sale mb-3">{product.badge}</span>}
            <h1 className="font-heading text-3xl md:text-4xl font-extrabold uppercase tracking-tight">{product.name}</h1>
            <div className="flex gap-3 items-center mt-4">
              <span className={`text-2xl font-bold ${product.original_price ? "text-sale" : "text-ink"}`}>{money(selectedVariant?.price ?? product.price)}</span>
              {product.original_price && <span className="text-gray-400 line-through text-lg">{money(product.original_price)}</span>}
            </div>

            {product.colors?.length > 0 && (
              <div className="mt-8">
                <p className="text-xs font-bold uppercase tracking-widest mb-3">Color: <span className="text-gray-500">{color}</span></p>
                <div className="flex gap-2 flex-wrap">
                  {product.colors.map((c) => (
                    <button key={c} onClick={() => handleColor(c)} className={`px-4 py-2 border text-xs font-bold uppercase tracking-wide transition-all ${color === c ? "border-ink bg-ink text-cream" : "border-gray-200 hover:border-ink"}`} data-testid={`color-${c}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8">
              <p className="text-xs font-bold uppercase tracking-widest mb-3">Size</p>
              <div className="flex gap-2 flex-wrap">
                {product.sizes.map((s) => (
                  <button key={s} onClick={() => setSize(s)} className={`min-w-12 h-12 px-3 border text-sm font-bold transition-all ${size === s ? "border-ink bg-ink text-cream" : "border-gray-200 hover:border-ink"}`} data-testid={`size-${s}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <button onClick={handleAdd} disabled={soldOut} className={`mt-10 w-full py-5 font-bold uppercase tracking-widest transition-colors ${soldOut ? "bg-gray-300 text-gray-600 cursor-not-allowed" : "bg-ink text-cream hover:bg-ink/85"}`} data-testid="add-to-cart-btn">
              {soldOut ? "Sold Out" : `Add To Bag — ${money(selectedVariant?.price ?? product.price)}`}
            </button>

            {product.description && (
              <div className="mt-8 border-t border-gray-100 pt-6">
                <p className="text-xs font-bold uppercase tracking-widest mb-3">Details</p>
                <p className="text-gray-600 leading-relaxed text-sm whitespace-pre-line line-clamp-6">{product.description}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 mt-8 text-center">
              <div className="flex flex-col items-center gap-2"><Truck size={20} /><span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Free Ship ${FREE_SHIP_THRESHOLD}+</span></div>
              <div className="flex flex-col items-center gap-2"><ShieldCheck size={20} /><span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Secure Checkout</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
